import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, Mail, Briefcase, Lock, MapPin, Clock, ArrowRight } from 'lucide-react';
import api from '../../utils/api';

const ROOMS = [
  { id: 'cucina', icon: '🍳', label: 'Cucina' },
  { id: 'soggiorno', icon: '🛋️', label: 'Soggiorno' },
  { id: 'camera', icon: '🛏️', label: 'Camera' },
  { id: 'bagno', icon: '🚿', label: 'Bagno' },
  { id: 'giardino', icon: '🌳', label: 'Giardino' },
];
const STYLES = [
  { id: 'moderno', label: 'Moderno' },
  { id: 'industriale', label: 'Industriale' },
  { id: 'scandinavo', label: 'Scandinavo' },
  { id: 'classico', label: 'Classico' },
  { id: 'boho', label: 'Boho' },
  { id: 'minimal', label: 'Minimal' },
];

const REQUEST_CATEGORIES = [
  'idraulico', 'elettricista', 'muratore', 'imbianchino',
  'falegname', 'architetto', 'geometra', 'avvocato',
  'commercialista', 'ingegnere', 'fabbro', 'giardiniere',
];

// Realistic-looking sample requests (anonymized — names/contacts blurred until login)
const SAMPLE_REQUESTS = {
  idraulico: [
    { id: 'r1', title: 'Sostituzione caldaia 24kW + manutenzione annuale', city: 'Milano', budget: '2.000 - 3.500 €', posted: '2h fa', urgency: 'urgente', desc: 'Caldaia in casa singola da 110m². Vorrei sostituirla con modello a condensazione ad alta efficienza. Servirebbe sopralluogo entro la settimana.' },
    { id: 'r2', title: 'Perdita nel bagno principale - intervento rapido', city: 'Roma', budget: '500 - 900 €', posted: '5h fa', urgency: 'oggi', desc: 'Acqua che cola dal soffitto del bagno al piano sotto. Probabile guasto allo scarico. Disponibilità subito.' },
    { id: 'r3', title: 'Installazione bagno completo nuovo appartamento', city: 'Torino', budget: '5.500 - 8.500 €', posted: '1g fa', urgency: 'questa settimana', desc: 'Ristrutturazione bagno cieco 4m². Sostituzione completa di sanitari, doccia walk-in, rubinetterie nuove. Materiali da scegliere insieme.' },
  ],
  elettricista: [
    { id: 'r4', title: 'Rifacimento impianto elettrico appartamento 90mq', city: 'Napoli', budget: '6.500 - 11.000 €', posted: '3h fa', urgency: 'questo mese', desc: 'Appartamento anni 60. Servono certificazione e tutto a norma con colonnine separate per ogni stanza, 25 punti luce.' },
    { id: 'r5', title: 'Installazione 8 prese smart in cucina nuova', city: 'Bologna', budget: '900 - 1.500 €', posted: '6h fa', urgency: 'questa settimana', desc: 'Cucina ristrutturata. Servono prese USB integrate sotto il piano lavoro e gestione domotica luci.' },
  ],
  muratore: [
    { id: 'r6', title: 'Demolizione parete + nuovo varco per cucina open space', city: 'Firenze', budget: '3.500 - 5.500 €', posted: '4h fa', urgency: 'questo mese', desc: 'Parete divisoria in mattoni forati di 4m. Servirebbe abbattimento, smaltimento e finitura intonaco.' },
    { id: 'r7', title: 'Ristrutturazione bagno chiavi in mano', city: 'Verona', budget: '11.000 - 16.000 €', posted: '2g fa', urgency: 'flessibile', desc: 'Bagno 6m² da rifare completamente: rivestimenti pavimento e parete, sanitari nuovi, doccia, controsoffitto.' },
  ],
  imbianchino: [
    { id: 'r8', title: 'Tinteggiatura completa appartamento 70mq', city: 'Genova', budget: '1.000 - 1.800 €', posted: '7h fa', urgency: 'questa settimana', desc: 'Appartamento vuoto. 4 stanze + corridoio. Bianco soffitti, color tortora pareti. Ho già il colore.' },
    { id: 'r9', title: 'Verniciatura facciata villa esterna', city: 'Bari', budget: '5.500 - 8.500 €', posted: '1g fa', urgency: 'questo mese', desc: 'Villa 2 piani facciata 180m² con stuccatura crepe e applicazione idropittura silossanica.' },
  ],
  falegname: [
    { id: 'r10', title: 'Realizzazione cucina su misura in rovere', city: 'Padova', budget: '14.000 - 22.000 €', posted: '5h fa', urgency: 'flessibile', desc: 'Cucina ad L su misura 3.20m + 2.40m con isola, rovere massello, top in laminam, elettrodomestici inclusi.' },
    { id: 'r11', title: 'Cabina armadio camera matrimoniale', city: 'Brescia', budget: '4.500 - 6.500 €', posted: '8h fa', urgency: 'questo mese', desc: 'Spazio 2.50x1.80m. Voglio cabina con anta scorrevole specchio, illuminazione led, cassettiera centrale.' },
  ],
  architetto: [
    { id: 'r12', title: 'Progetto ristrutturazione casa 130mq con SCIA', city: 'Milano', budget: '5.500 - 9.000 €', posted: '4h fa', urgency: 'questo mese', desc: 'Devo ristrutturare casa appena acquistata. Servirebbe progetto completo + direzione lavori + pratica SCIA in Comune.' },
    { id: 'r13', title: 'Render fotorealistici nuovo loft open space', city: 'Roma', budget: '1.500 - 2.800 €', posted: '12h fa', urgency: 'questa settimana', desc: 'Loft 65m² da arredare. Vorrei 3-4 render in stile industrial scandinavo per scegliere arredi.' },
  ],
  geometra: [
    { id: 'r14', title: 'Pratica catastale + accatastamento variazione interna', city: 'Bologna', budget: '800 - 1.400 €', posted: '6h fa', urgency: 'questo mese', desc: 'Ho fatto modifiche interne (spostamento pareti). Servirebbe aggiornamento catastale e pratica DOCFA.' },
  ],
  avvocato: [
    { id: 'r15', title: 'Consulenza compravendita immobiliare + atto notarile', city: 'Milano', budget: '1.200 - 2.200 €', posted: '3h fa', urgency: 'questa settimana', desc: 'Sto comprando un immobile usato a Milano. Servirebbe controllo completo del rogito e assistenza pre-rogito.' },
    { id: 'r16', title: 'Causa condominiale per spese non dovute', city: 'Torino', budget: '2.800 - 5.000 €', posted: '1g fa', urgency: 'questo mese', desc: 'Amministratore mi addebita spese fuori bilancio. Vorrei consulenza + avvio causa civile se necessario.' },
  ],
  commercialista: [
    { id: 'r17', title: 'Apertura partita IVA artigiano + regime forfettario', city: 'Verona', budget: '600 - 1.000 €', posted: '4h fa', urgency: 'questa settimana', desc: 'Inizio attività di artigiano (lavori in casa). Mi servirebbe apertura PIVA + iscrizione INPS + scelta regime fiscale.' },
  ],
  ingegnere: [
    { id: 'r18', title: 'Calcolo strutturale ampliamento veranda', city: 'Bari', budget: '1.400 - 2.500 €', posted: '5h fa', urgency: 'questo mese', desc: 'Voglio chiudere veranda esistente in alluminio con struttura più solida. Servirebbe progetto e calcoli.' },
  ],
  fabbro: [
    { id: 'r19', title: 'Cancello carraio motorizzato 4m + automazione', city: 'Brescia', budget: '4.000 - 6.500 €', posted: '7h fa', urgency: 'questo mese', desc: 'Cancello scorrevole acciaio zincato verniciato + motore + telecomandi + fotocellule.' },
  ],
  giardiniere: [
    { id: 'r20', title: 'Manutenzione mensile giardino 200mq + impianto irrigazione', city: 'Roma', budget: '180 - 320 €/mese', posted: '6h fa', urgency: 'flessibile', desc: 'Giardino con prato + siepi + 4 alberi. Servono 2 visite/mese e revisione impianto irrigazione.' },
  ],
};

const trackCta = (button_id) => {
  api.post('/api/track/cta', { button_id, page: 'landing' }).catch(() => {});
};

const TabButton = ({ active, onClick, icon: Icon, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 px-4 py-3.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
      active ? 'bg-[#10B981] text-white shadow-lg shadow-[#10B981]/30' : 'text-slate-400 hover:text-white hover:bg-white/5'
    }`}
  >
    <Icon className="w-4 h-4" /> {children}
  </button>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
      active ? 'bg-[#10B981] text-white border-[#10B981]' : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
    }`}
  >
    {children}
  </button>
);

const LandingTabs = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('aistudio');

  // AI Studio state
  const [aiMode, setAiMode] = useState('zero'); // 'zero' | 'photo'
  const [aiRoom, setAiRoom] = useState('cucina');
  const [aiStyle, setAiStyle] = useState('moderno');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPhoto, setAiPhoto] = useState(null); // base64 dataURL

  // Crea Richiesta state
  const [reqCat, setReqCat] = useState('');
  const [reqCity, setReqCity] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqPhoto, setReqPhoto] = useState(null); // base64 dataURL

  // Trova Pro state
  const [searchQ, setSearchQ] = useState('');
  const [searchCity, setSearchCity] = useState('');

  // Richieste per te state
  const [feedCat, setFeedCat] = useState('idraulico');

  const switchTab = (t) => {
    setTab(t);
    trackCta(`landing_tab_${t}`);
  };

  const fileToBase64 = (file) => new Promise((res, rej) => {
    if (file.size > 6 * 1024 * 1024) { rej(new Error('Foto troppo grande (max 6MB)')); return; }
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const handleAiPhotoUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const b64 = await fileToBase64(f); setAiPhoto(b64); trackCta('landing_aistudio_upload'); }
    catch (err) { alert(err.message || 'Errore caricamento foto'); }
  };

  const handleReqPhotoUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const b64 = await fileToBase64(f); setReqPhoto(b64); trackCta('landing_request_upload'); }
    catch (err) { alert(err.message || 'Errore caricamento foto'); }
  };

  // ----- HANDLERS -----
  const handleAIGenerate = () => {
    trackCta(aiMode === 'photo' ? 'landing_aistudio_generate_photo' : 'landing_aistudio_generate_zero');
    try {
      sessionStorage.setItem('ai_studio_draft', JSON.stringify({
        mode: aiMode, room_type: aiRoom, style: aiStyle, custom_prompt: aiPrompt,
        original_image: aiMode === 'photo' ? aiPhoto : null, ts: Date.now(),
      }));
    } catch {}
    navigate('/register?redirect=/ai/redesign');
  };

  const handleReqPublish = () => {
    trackCta('landing_request_publish');
    try {
      sessionStorage.setItem('request_draft', JSON.stringify({
        category: reqCat, city: reqCity, description: reqDesc,
        ai_image: reqPhoto, ts: Date.now(),
      }));
    } catch {}
    navigate('/register?redirect=/requests/create');
  };

  const handleReqAIModal = () => {
    trackCta('landing_request_ai_compile');
    try {
      sessionStorage.setItem('request_draft', JSON.stringify({
        category: reqCat, city: reqCity, description: reqDesc,
        ai_image: reqPhoto, ai: true, ts: Date.now(),
      }));
    } catch {}
    navigate('/register?redirect=/requests/create?ai=1');
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    trackCta('landing_search_pro');
    const params = new URLSearchParams();
    if (searchQ) params.set('q', searchQ);
    if (searchCity) params.set('city', searchCity);
    navigate(`/search?${params.toString()}`);
  };

  const handleViewRequest = (rid) => {
    trackCta('landing_request_view_detail');
    navigate(`/register?redirect=/requests`);
  };

  const requests = SAMPLE_REQUESTS[feedCat] || [];

  return (
    <section className="relative bg-[#0B1220] py-10 sm:py-14" data-testid="landing-tabs-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-5">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-3xl font-black text-white tracking-tight">
            Cosa vuoi fare <span className="text-[#10B981]">oggi</span>?
          </h2>
          <p className="text-sm text-slate-400 mt-2">Scegli un punto di partenza — funziona tutto subito, registrazione solo quando serve.</p>
        </div>

        <div className="bg-[#0F1729] border border-white/10 rounded-2xl p-2 sm:p-3 shadow-2xl shadow-black/40">
          {/* TABS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 bg-black/20 rounded-xl p-1.5">
            <TabButton active={tab === 'pro'} onClick={() => switchTab('pro')} icon={Search} testid="landing-tab-pro">Trova Pro</TabButton>
            <TabButton active={tab === 'aistudio'} onClick={() => switchTab('aistudio')} icon={Sparkles} testid="landing-tab-aistudio">AI Studio</TabButton>
            <TabButton active={tab === 'request'} onClick={() => switchTab('request')} icon={Mail} testid="landing-tab-request">Crea Richiesta</TabButton>
            <TabButton active={tab === 'feed'} onClick={() => switchTab('feed')} icon={Briefcase} testid="landing-tab-feed">Richieste per te</TabButton>
          </div>

          {/* PANEL: Trova Pro */}
          {tab === 'pro' && (
            <div className="p-5 sm:p-8" data-testid="landing-panel-pro">
              <h3 className="text-lg font-bold text-white mb-1">Trova il professionista giusto per te</h3>
              <p className="text-sm text-slate-400 mb-5">Cerca tra 81.611 professionisti verificati — risposta in 24h.</p>
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                <input
                  value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Cosa cerchi? (es. idraulico, elettricista...)"
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#10B981]"
                  data-testid="landing-search-q"
                />
                <input
                  value={searchCity} onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="Dove? (es. Milano)"
                  className="sm:w-48 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#10B981]"
                  data-testid="landing-search-city"
                />
                <button type="submit" className="px-6 py-3 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2" data-testid="landing-search-submit">
                  <Search className="w-4 h-4" /> Cerca
                </button>
              </form>
              <div className="mt-5 flex flex-wrap gap-2">
                {['idraulico', 'elettricista', 'muratore', 'architetto', 'avvocato'].map(s => (
                  <button key={s} onClick={() => { setSearchQ(s); trackCta(`landing_quicksearch_${s}`); }} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-xs text-slate-300 rounded-full border border-white/10">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PANEL: AI Studio */}
          {tab === 'aistudio' && (
            <div className="p-5 sm:p-8" data-testid="landing-panel-aistudio">
              {/* Sub-tab toggle: Crea da zero / Trasforma foto */}
              <div className="inline-flex bg-black/30 rounded-xl p-1 mb-5">
                <button
                  onClick={() => { setAiMode('zero'); trackCta('landing_aistudio_mode_zero'); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${aiMode === 'zero' ? 'bg-[#10B981] text-white' : 'text-slate-400 hover:text-white'}`}
                  data-testid="landing-ai-mode-zero"
                >🎨 Crea da Zero</button>
                <button
                  onClick={() => { setAiMode('photo'); trackCta('landing_aistudio_mode_photo'); }}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${aiMode === 'photo' ? 'bg-[#10B981] text-white' : 'text-slate-400 hover:text-white'}`}
                  data-testid="landing-ai-mode-photo"
                >📸 Trasforma Foto</button>
              </div>
              <div className="grid lg:grid-cols-2 gap-6 items-start">
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">{aiMode === 'photo' ? 'Carica una foto della tua stanza' : 'AI Studio · Trasforma le tue idee'}</h3>
                  <p className="text-sm text-slate-400 mb-4">{aiMode === 'photo' ? 'Carica una foto reale del tuo ambiente, scegli lo stile e l\'AI la trasforma.' : 'Scegli stanza e stile, poi clicca Genera. La tua idea diventa immagine in 20s.'}</p>

                  {aiMode === 'photo' && (
                    <div className="mb-3">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-bold">La tua foto</p>
                      {aiPhoto ? (
                        <div className="relative">
                          <img src={aiPhoto} alt="Caricata" className="w-full h-40 object-cover rounded-xl" />
                          <button onClick={() => setAiPhoto(null)} className="absolute top-2 right-2 bg-black/70 text-white text-xs px-3 py-1 rounded-full">✕ Rimuovi</button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/15 rounded-xl cursor-pointer hover:bg-white/5 transition" data-testid="landing-ai-upload-zone">
                          <span className="text-4xl mb-1">📷</span>
                          <span className="text-sm text-slate-300 font-semibold">Carica foto stanza</span>
                          <span className="text-xs text-slate-500 mt-1">JPG/PNG · max 6 MB</span>
                          <input type="file" accept="image/*" onChange={handleAiPhotoUpload} className="hidden" data-testid="landing-ai-file" />
                        </label>
                      )}
                    </div>
                  )}

                  {aiMode === 'zero' && (
                    <div className="mb-3">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-bold">Stanza</p>
                      <div className="flex flex-wrap gap-2">
                        {ROOMS.map(r => (
                          <Chip key={r.id} active={aiRoom === r.id} onClick={() => setAiRoom(r.id)} testid={`landing-room-${r.id}`}>
                            {r.icon} {r.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-bold">Stile</p>
                    <div className="flex flex-wrap gap-2">
                      {STYLES.map(s => (
                        <Chip key={s.id} active={aiStyle === s.id} onClick={() => setAiStyle(s.id)} testid={`landing-style-${s.id}`}>
                          {s.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <input
                    value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={aiMode === 'photo' ? 'Cosa vuoi cambiare? (es. parete bianca, pavimento parquet)' : 'Aggiungi un dettaglio (opzionale): luminosa con isola...'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#10B981] mb-4"
                    data-testid="landing-ai-prompt"
                  />
                  <button
                    onClick={handleAIGenerate}
                    disabled={aiMode === 'photo' && !aiPhoto}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-[#10B981] to-[#06B6D4] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 shadow-lg shadow-[#10B981]/30"
                    data-testid="landing-ai-generate"
                  >
                    <Sparkles className="w-4 h-4" /> {aiMode === 'photo' ? 'Trasforma con AI' : 'Genera con AI'} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-slate-500 mt-2 text-center">Registrazione gratuita: 5 generazioni gratis al mese</p>
                </div>
                <div className="hidden lg:flex bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 rounded-2xl h-72 items-end p-4 relative overflow-hidden">
                  {aiMode === 'photo' && aiPhoto ? (
                    <img src={aiPhoto} alt="Preview" className="absolute inset-0 w-full h-full object-cover rounded-2xl opacity-90" />
                  ) : (
                    <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22600%22 height=%22300%22><rect width=%22600%22 height=%22300%22 fill=%22none%22/><g opacity=%220.6%22><rect x=%22120%22 y=%22120%22 width=%22360%22 height=%22120%22 fill=%22white%22 opacity=%220.2%22 rx=%228%22/><rect x=%22160%22 y=%22150%22 width=%2280%22 height=%2280%22 fill=%22white%22 opacity=%220.4%22 rx=%224%22/></g></svg>')] bg-cover" />
                  )}
                  <div className="relative z-10 w-full">
                    <p className="text-white/90 font-bold text-sm">Anteprima trasformazione</p>
                    <p className="text-white/60 text-xs">{aiMode === 'photo' ? 'Foto reale → Stile' : ROOMS.find(r => r.id === aiRoom)?.label} · {STYLES.find(s => s.id === aiStyle)?.label}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PANEL: Crea Richiesta */}
          {tab === 'request' && (
            <div className="p-5 sm:p-8" data-testid="landing-panel-request">
              <h3 className="text-lg font-bold text-white mb-1">Crea una richiesta — i pro ti contattano</h3>
              <p className="text-sm text-slate-400 mb-5">Pubblica cosa ti serve: i professionisti adatti ti scrivono entro 24h.</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <select value={reqCat} onChange={(e) => setReqCat(e.target.value)} className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:ring-2 focus:ring-[#10B981]" data-testid="landing-req-cat">
                  <option value="">Categoria…</option>
                  {REQUEST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={reqCity} onChange={(e) => setReqCity(e.target.value)} placeholder="Città" className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#10B981]" data-testid="landing-req-city" />
              </div>
              <textarea value={reqDesc} onChange={(e) => setReqDesc(e.target.value)} placeholder="Descrivi cosa ti serve (es. Sostituire caldaia 24kW...)" className="w-full min-h-[80px] px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#10B981] resize-none mb-3" data-testid="landing-req-desc" />

              {/* AI photo upload — visible always */}
              <div className="bg-gradient-to-br from-emerald-900/30 to-cyan-900/30 border border-[#10B981]/40 rounded-xl p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#10B981]" />
                  <span className="text-xs text-[#86EFAC] font-bold">AI HELPER · Compila la richiesta partendo da una foto</span>
                </div>
                {reqPhoto ? (
                  <div className="relative">
                    <img src={reqPhoto} alt="Caricata" className="w-full h-32 object-cover rounded-lg" />
                    <button onClick={() => setReqPhoto(null)} className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">✕</button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center w-full h-20 border border-dashed border-[#10B981]/50 rounded-lg cursor-pointer hover:bg-[#10B981]/5 transition" data-testid="landing-req-upload-zone">
                    <span className="text-xs text-slate-300">📷 Aggiungi foto del problema (l'AI la analizza)</span>
                    <input type="file" accept="image/*" onChange={handleReqPhotoUpload} className="hidden" data-testid="landing-req-file" />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={handleReqAIModal} className="px-5 py-3 bg-gradient-to-r from-[#10B981] to-[#06B6D4] hover:opacity-90 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 shadow-lg shadow-[#10B981]/30" data-testid="landing-req-ai">
                  <Sparkles className="w-4 h-4" /> Compila con AI
                </button>
                <button onClick={handleReqPublish} className="px-5 py-3 bg-white/10 hover:bg-white/15 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 border border-white/20" data-testid="landing-req-publish">
                  Pubblica <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* PANEL: Richieste per te */}
          {tab === 'feed' && (
            <div className="p-5 sm:p-8" data-testid="landing-panel-feed">
              <h3 className="text-lg font-bold text-white mb-1">Richieste adatte a te</h3>
              <p className="text-sm text-slate-400 mb-4">Scegli una categoria e vedi le richieste reali pubblicate dagli utenti. Registrati per vedere nome, contatti e candidarti.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {REQUEST_CATEGORIES.map(c => (
                  <Chip key={c} active={feedCat === c} onClick={() => { setFeedCat(c); trackCta(`landing_feed_cat_${c}`); }} testid={`landing-feed-cat-${c}`}>
                    {c}
                  </Chip>
                ))}
              </div>
              {requests.length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                  <p className="text-sm text-slate-400">Nessuna richiesta in questa categoria al momento. Prova un'altra.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {requests.map(r => (
                    <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/[0.07] transition-colors relative">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-sm font-bold text-white leading-snug">{r.title}</h4>
                        {r.urgency === 'urgente' || r.urgency === 'oggi' ? (
                          <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full whitespace-nowrap">{r.urgency}</span>
                        ) : (
                          <span className="text-[10px] uppercase font-bold bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full whitespace-nowrap">{r.urgency}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mb-3 line-clamp-2">{r.desc}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.posted}</span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-white/5">
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Budget</p>
                          <p className="text-sm font-bold text-[#10B981]">{r.budget}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="select-none text-slate-500 text-xs blur-sm" aria-hidden>★★★★★ User #****</span>
                          <button
                            onClick={() => handleViewRequest(r.id)}
                            className="ml-2 px-3 py-1.5 bg-[#10B981] hover:bg-[#059669] text-white text-xs font-bold rounded-lg flex items-center gap-1"
                            data-testid={`landing-feed-cta-${r.id}`}
                          >
                            <Lock className="w-3 h-3" /> Vedi
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 text-center">
                <button onClick={() => { trackCta('landing_feed_register'); navigate('/register?redirect=/requests'); }} className="px-6 py-3 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl text-sm inline-flex items-center gap-2" data-testid="landing-feed-register">
                  Registrati gratis e vedi tutte le richieste <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-slate-500 text-center mt-4 italic">
                * Richieste mostrate a scopo illustrativo. Le richieste reali sono visibili dopo la registrazione gratuita.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LandingTabs;
